import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { auth } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createLoginSchema } from '../schemas/loginSchema';
import { useAuth } from '../context/AuthContext';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LoginFormData } from '../types';
import { usePageTitle } from '../hooks/usePageTitle';

const Login = () => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(createLoginSchema(t)) as Resolver<LoginFormData>,
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [, setCookie] = useCookies(['authToken']);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  usePageTitle('Login');

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setResetError(t('login.emailRequired'));
      return;
    }

    setResetLoading(true);
    setResetSuccess(false);
    try {
      await auth.sendPasswordResetEmail(resetEmail);
      setResetSuccess(true);
      setResetError('');
      setTimeout(() => {
        setForgotPasswordOpen(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error(t('error.console.errorFetchingUserData'), error);
      switch (error.code) {
        case 'auth/user-not-found':
          setResetError(t('login.userNotFound'));
          break;
        case 'auth/invalid-email':
          setResetError(t('login.invalidEmail'));
          break;
        case 'auth/network-request-failed':
          setResetError(t('login.networkError'));
          break;
        default:
          setResetError(t('login.resetPasswordError'));
      }
    } finally {
      setResetLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setLoginError('');
    setIsLoading(true);
    try {
      const userCredential = await auth.signInWithEmailAndPassword(
        data.email,
        data.password
      );
      const user = userCredential.user;
      const token = await user?.getIdToken();

      if (data.remember) {
        const expires = new Date(Date.now() + 1000 * 60 * 60 * 24 * 30);
        setCookie('authToken', token, {
          path: '/',
          secure: true,
          sameSite: 'lax',
          expires,
        });
      } else {
        setCookie('authToken', token, {
          path: '/',
          secure: true,
          sameSite: 'lax',
        });
      }

      navigate('/dashboard');
    } catch (error: any) {
      console.error(error);
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setLoginError(t('login.invalidEmailOrPassword'));
          break;
        case 'auth/too-many-requests':
          setLoginError(t('login.tooManyAttempts'));
          break;
        case 'auth/network-request-failed':
          setLoginError(t('login.networkError'));
          break;
        default:
          setLoginError(t('login.genericError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          textAlign="center"
          color="primary"
        >
          {t('login.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 4 }}
        >
          {t('login.description')}
        </Typography>

        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          {loginError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loginError}
            </Alert>
          )}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('login.email')}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('login.password')}
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label={
                              showPassword
                                ? t('login.hidePassword')
                                : t('login.showPassword')
                            }
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Controller
                name="remember"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} />}
                    label={t('login.remember')}
                  />
                )}
              />
              <Typography
                variant="body2"
                color="primary"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => {
                  setForgotPasswordOpen(true);
                }}
              >
                {t('login.forgotPassword')}
              </Typography>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('login.login')
              )}
            </Button>
          </form>
        </Paper>
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('login.noAccount')}{' '}
            <Link to="/signup" style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {t('login.signUp')}
              </Typography>
            </Link>
          </Typography>
        </Box>
      </Box>
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {t('login.resetPassword')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('login.resetPasswordEmailSent')}
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('login.resetPasswordTitle')}
              </Typography>

              <TextField
                fullWidth
                label={t('login.email')}
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                error={!!resetError}
                helperText={resetError}
                autoFocus
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setForgotPasswordOpen(false)}
            disabled={resetLoading}
          >
            {t('login.cancel')}
          </Button>
          {!resetSuccess && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={resetLoading || !resetEmail.trim()}
            >
              {resetLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('login.send')
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
